// src/components/Budget/SharedCategoryDetails.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Stack,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Paper,
  Tooltip,
  useMediaQuery,
  Drawer,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  Star as StarIcon,
  Delete as DeleteIcon,
  PersonRemove as PersonRemoveIcon,
  Settings as SettingsIcon,
  ExitToApp as ExitIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSharedCategory } from '../../contexts/SharedCategoryContext';
import { useAuth } from '../../contexts/AuthContext';
import { SpendingBreakdownChart } from './SpendingBreakdownChart';
import { InviteCodeDisplay } from './InviteCodeDisplay';

export const SharedCategoryDetails = ({ open, onClose, sharedCategoryId }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const {
    fetchSharedCategoryDetails,
    leaveSharedCategory,
    removeMember,
    updateSettings,
    regenerateInviteCode,
    deleteSharedCategory,
    loading,
  } = useSharedCategory();

  const [categoryDetails, setCategoryDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [error, setError] = useState('');
  const [showInviteCode, setShowInviteCode] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editingBudget, setEditingBudget] = useState(false);
  const [newBudgetAmount, setNewBudgetAmount] = useState('');

  // Fetch category details
  useEffect(() => {
    if (open && sharedCategoryId) {
      loadCategoryDetails();
    }
  }, [open, sharedCategoryId]);

  const loadCategoryDetails = async () => {
    setLoadingDetails(true);
    setError('');
    try {
      const details = await fetchSharedCategoryDetails(sharedCategoryId);
      setCategoryDetails(details.sharedCategory);
    } catch (err) {
      setError('Failed to load category details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const isOwner = categoryDetails?.owner?._id === user?._id;

  const handleLeave = async () => {
    if (!window.confirm(t('sharedCategories.confirmLeave', 'Are you sure you want to leave this shared category?'))) {
      return;
    }

    try {
      await leaveSharedCategory(sharedCategoryId);
      setSnackbar({ open: true, message: 'Left shared category', severity: 'success' });
      onClose();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to leave category', severity: 'error' });
    }
  };

  const handleUnshare = async () => {
    if (!window.confirm(t('sharedCategories.confirmUnshare', 'Are you sure you want to unshare this category? All members will lose access and the category will become private again.'))) {
      return;
    }

    try {
      await deleteSharedCategory(sharedCategoryId);
      setSnackbar({ open: true, message: 'Category unshared successfully', severity: 'success' });
      setTimeout(() => {
        onClose();
        window.location.reload(); // Refresh to update user preferences
      }, 1000);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to unshare category', severity: 'error' });
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm(t('sharedCategories.confirmRemoveMember', 'Remove this member from the shared category?'))) {
      return;
    }

    try {
      await removeMember(sharedCategoryId, userId);
      setSnackbar({ open: true, message: 'Member removed', severity: 'success' });
      loadCategoryDetails(); // Reload to update member list
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to remove member', severity: 'error' });
    }
  };

  const handleUpdateBudgetMode = async (newMode) => {
    try {
      await updateSettings(sharedCategoryId, { budgetMode: newMode });
      setCategoryDetails({ ...categoryDetails, budgetMode: newMode });
      setSnackbar({ open: true, message: 'Budget mode updated', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update budget mode', severity: 'error' });
    }
  };

  const handleUpdateTransparency = async (newLevel) => {
    try {
      await updateSettings(sharedCategoryId, { transparencyLevel: newLevel });
      setCategoryDetails({ ...categoryDetails, transparencyLevel: newLevel });
      setSnackbar({ open: true, message: 'Transparency updated', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update transparency', severity: 'error' });
    }
  };

  const handleUpdateBudget = async () => {
    const amount = parseFloat(newBudgetAmount);
    if (isNaN(amount) || amount < 0) {
      setSnackbar({ open: true, message: 'Please enter a valid budget amount', severity: 'error' });
      return;
    }

    try {
      await updateSettings(sharedCategoryId, { sharedLimit: amount });
      setCategoryDetails({ ...categoryDetails, sharedLimit: amount });
      setSnackbar({ open: true, message: 'Budget updated successfully', severity: 'success' });
      setEditingBudget(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update budget', severity: 'error' });
    }
  };

  const handleRegenerateCode = async () => {
    try {
      const result = await regenerateInviteCode(sharedCategoryId);
      setCategoryDetails({
        ...categoryDetails,
        inviteCode: result.inviteCode,
        inviteCodeExpiry: result.inviteCodeExpiry,
      });
      setSnackbar({ open: true, message: 'New invite code generated', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to generate code', severity: 'error' });
    }
  };

  const handleCopyInviteCode = async () => {
    try {
      await navigator.clipboard.writeText(categoryDetails?.inviteCode);
      setSnackbar({ open: true, message: 'Invite code copied!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to copy code', severity: 'error' });
    }
  };

  // Prepare spending data for chart
  const spendingData = categoryDetails?.spendingBreakdown?.map((item) => ({
    userId: item.userId,
    userName: item.userName,
    userAvatar: item.userAvatar,
    amount: item.totalSpent,
  })) || [];

  const DialogComponent = isMobile ? Drawer : Dialog;
  const dialogProps = isMobile
    ? {
        anchor: 'bottom',
        PaperProps: {
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '90vh',
          },
        },
      }
    : {
        maxWidth: 'md',
        fullWidth: true,
        PaperProps: { sx: { borderRadius: 3 } },
      };

  return (
    <>
      <DialogComponent open={open} onClose={onClose} {...dialogProps}>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" fontWeight="600">
                {categoryDetails?.name || 'Shared Category'}
              </Typography>
              <Chip
                label={t('sharedCategories.shared', 'Shared')}
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Stack>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <Stack spacing={3}>
              {/* Spending Breakdown Chart */}
              {spendingData.length > 0 && (
                <SpendingBreakdownChart
                  spendingData={spendingData}
                  totalBudget={categoryDetails?.sharedLimit || 0}
                />
              )}

              {/* Participants List (Owner + Members) */}
              <Box>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                  {t('sharedCategories.participants', 'Participants')} ({(categoryDetails?.members?.length || 0) + 1})
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2,
                  }}
                >
                  <List>
                    {(() => {
                      // Combine owner + members into single list (filter owner out of members to avoid duplication)
                      const ownerId = categoryDetails?.owner?._id;
                      const allParticipants = [
                        // Add owner first (if exists)
                        ...(categoryDetails?.owner ? [{
                          userId: categoryDetails.owner,
                          role: 'owner',
                          joinedAt: categoryDetails.createdAt
                        }] : []),
                        // Then add members (excluding owner if they're in members array)
                        ...(categoryDetails?.members || [])
                          .filter(m => m.userId?._id !== ownerId)
                          .map(m => ({
                            ...m,
                            role: m.role || 'member'
                          }))
                      ];

                      return allParticipants.map((participant, index) => (
                        <React.Fragment key={participant.userId?._id || index}>
                          {index > 0 && <Divider />}
                          <ListItem
                            secondaryAction={
                              isOwner && participant.role !== 'owner' ? (
                                <Tooltip title="Remove member">
                                  <IconButton
                                    edge="end"
                                    onClick={() => handleRemoveMember(participant.userId?._id)}
                                    color="error"
                                    size="small"
                                  >
                                    <PersonRemoveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : null
                            }
                          >
                            <ListItemAvatar>
                              <Avatar
                                src={participant.userId?.profilePicture}
                                sx={{
                                  bgcolor:
                                    participant.role === 'owner'
                                      ? theme.palette.warning.main
                                      : theme.palette.primary.main,
                                }}
                              >
                                {(participant.userId?.name || 'U').charAt(0).toUpperCase()}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Typography variant="body2" fontWeight="500">
                                    {participant.userId?.name || 'User'}
                                  </Typography>
                                  {participant.role === 'owner' && (
                                    <Chip
                                      icon={<StarIcon />}
                                      label={t('sharedCategories.owner', 'Owner')}
                                      size="small"
                                      color="warning"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  )}
                                  {participant.userId?._id === user?._id && (
                                    <Chip
                                      label={t('sharedCategories.you', 'You')}
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Stack>
                              }
                              secondary={`Joined ${new Date(
                                participant.joinedAt
                              ).toLocaleDateString()}`}
                            />
                          </ListItem>
                        </React.Fragment>
                      ));
                    })()}
                  </List>
                </Paper>
              </Box>

              {/* Settings (Owner Only) */}
              {isOwner && (
                <>
                  <Divider />
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                      <SettingsIcon color="action" />
                      <Typography variant="subtitle1" fontWeight="600">
                        {t('sharedCategories.settings', 'Settings')}
                      </Typography>
                    </Stack>

                    <Stack spacing={2}>
                      {/* Budget Mode */}
                      <Box>
                        <Typography variant="body2" fontWeight="500" gutterBottom>
                          {t('sharedCategories.budgetMode', 'Budget Mode')}
                        </Typography>
                        <ToggleButtonGroup
                          value={categoryDetails?.budgetMode || 'shared'}
                          exclusive
                          onChange={(e, val) => val && handleUpdateBudgetMode(val)}
                          size="small"
                          fullWidth
                        >
                          <ToggleButton value="shared">{t('sharedCategories.sharedLimit', 'Shared Limit')}</ToggleButton>
                          <ToggleButton value="individual">{t('sharedCategories.individualLimits', 'Individual Limits')}</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>

                      {/* Shared Budget Amount */}
                      <Box>
                        <Typography variant="body2" fontWeight="500" gutterBottom>
                          {t('sharedCategories.sharedBudget', 'Shared Budget')}
                        </Typography>
                        {editingBudget ? (
                          <Stack direction="row" spacing={1}>
                            <TextField
                              size="small"
                              type="number"
                              value={newBudgetAmount}
                              onChange={(e) => setNewBudgetAmount(e.target.value)}
                              placeholder={`${categoryDetails?.sharedLimit || 0}`}
                              fullWidth
                              InputProps={{
                                startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>$</Typography>
                              }}
                            />
                            <Button size="small" variant="contained" onClick={handleUpdateBudget}>
                              {t('common.save', 'Save')}
                            </Button>
                            <Button size="small" onClick={() => setEditingBudget(false)}>
                              {t('common.cancel', 'Cancel')}
                            </Button>
                          </Stack>
                        ) : (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6" color="primary">
                              ${categoryDetails?.sharedLimit || 0}
                            </Typography>
                            <Button 
                              size="small" 
                              startIcon={<EditIcon />}
                              onClick={() => {
                                setNewBudgetAmount(categoryDetails?.sharedLimit || 0);
                                setEditingBudget(true);
                              }}
                            >
                              {t('common.edit', 'Edit')}
                            </Button>
                          </Stack>
                        )}
                      </Box>

                      {/* Transparency */}
                      <Box>
                        <Typography variant="body2" fontWeight="500" gutterBottom>
                          {t('sharedCategories.transparencyLevel', 'Transparency Level')}
                        </Typography>
                        <ToggleButtonGroup
                          value={categoryDetails?.transparencyLevel || 'full'}
                          exclusive
                          onChange={(e, val) => val && handleUpdateTransparency(val)}
                          size="small"
                          fullWidth
                        >
                          <ToggleButton value="full">
                            <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
                            {t('sharedCategories.full', 'Full')}
                          </ToggleButton>
                          <ToggleButton value="private">
                            <VisibilityOffIcon fontSize="small" sx={{ mr: 0.5 }} />
                            {t('sharedCategories.private', 'Private')}
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>

                      {/* Invite Code Management */}
                      <Box>
                        <Typography variant="body2" fontWeight="500" gutterBottom>
                          {t('sharedCategories.inviteCode', 'Invite Code')}
                        </Typography>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography
                              variant="h6"
                              fontFamily="monospace"
                              letterSpacing={2}
                            >
                              {categoryDetails?.inviteCode}
                            </Typography>
                            <IconButton size="small" onClick={handleCopyInviteCode}>
                              <CopyIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={handleRegenerateCode}>
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {t('sharedCategories.expires', 'Expires:')}{' '}
                            {new Date(categoryDetails?.inviteCodeExpiry).toLocaleDateString()}
                          </Typography>
                        </Paper>
                      </Box>
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          {isOwner ? (
            <Button
              onClick={handleUnshare}
              color="error"
              startIcon={<ExitIcon />}
              disabled={loading}
            >
              {t('sharedCategories.unshareCategory', 'Unshare Category')}
            </Button>
          ) : (
            <Button
              onClick={handleLeave}
              color="error"
              startIcon={<ExitIcon />}
              disabled={loading}
            >
              {t('sharedCategories.leaveCategory', 'Leave Category')}
            </Button>
          )}
          <Button onClick={onClose}>{t('common.close', 'Close')}</Button>
        </DialogActions>
      </DialogComponent>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

SharedCategoryDetails.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sharedCategoryId: PropTypes.string,
};

export default SharedCategoryDetails;
